/* eslint-disable react/jsx-props-no-spreading */
import {InspectorControls} from '@wordpress/block-editor';
import {ToggleControl, PanelBody, SelectControl, TextControl} from '@wordpress/components';
import { useEffect } from '@wordpress/element';
import {__} from '@wordpress/i18n';
import {createHigherOrderComponent} from '@wordpress/compose';
import {addFilter} from '@wordpress/hooks';

const addApplyNowButtonAttributes = (settings, name) => {
    if (name === 'core/button') {
        return {
            ...settings,
            attributes: {
                ...settings.attributes,
                enableTrackingCode: {
                    type: 'boolean',
                    default: false,
                },
                trackingCardType: {
                    type: 'string',
                    default: 'classic',
                },
                trackingQuery: {
                    type: 'string',
                    default: '',
                },
                trackingFinalUrl: {
                    type: 'string',
                    default: '',
                },
            },
        };
    }
    return settings;
};

addFilter(
    'blocks.registerBlockType',
    'apply-now-button/add-button-meta-attributes',
    addApplyNowButtonAttributes
);

const withButtonControls = createHigherOrderComponent((BlockEdit) => {
    return function ButtonEditWithControls(props) {
        const {name, attributes, setAttributes} = props;

        if (name !== 'core/button') {
            return <BlockEdit {...props} />;
        }

        const {enableTrackingCode, trackingCardType, trackingQuery, trackingFinalUrl} = attributes;

        const getDefaultApplyUrl = (type) => {
            const base = (window.DRTrackingCodesApply && window.DRTrackingCodesApply.baseUrl) || 'https://secure.chase.com/web/oao/application/card';
            const safeType = ['classic', 'premier', 'inspire'].includes(type) ? type : 'classic';
            const sep = base.includes('?') ? '&' : '?';
            return `${base}${sep}sourceCode=((spid_${safeType}))&cellCode=((cell))`;
        };

        const buildFinalUrl = (baseUrl, query) => {
            const q = (query || '').trim();
            if (!q) return baseUrl;
            // Allow users to paste with or without leading ? or &
            const sep = baseUrl.includes('?') ? '&' : '?';
            const cleaned = q.replace(/^\?+/, '').replace(/^&+/, '');
            return `${baseUrl}${sep}${cleaned}`;
        };

        const baseForSync = getDefaultApplyUrl(trackingCardType);
        const computedForSync = buildFinalUrl(baseForSync, trackingQuery);
        useEffect(() => {
            if (enableTrackingCode && computedForSync !== trackingFinalUrl) {
                setAttributes({ trackingFinalUrl: computedForSync });
            }
        }, [enableTrackingCode, trackingCardType, trackingQuery]);

        return (
            <>
                <BlockEdit {...props} />
                <InspectorControls>
                    <PanelBody title={__('Tracking Code', ' dr-tracking-codes')} initialOpen>
                        <ToggleControl
                            __nextHasNoMarginBottom
                            __next40pxDefaultSize
                            label={__('Enable', ' dr-tracking-codes')}
                            checked={!!enableTrackingCode}
                            onChange={(isChecked) => setAttributes({ enableTrackingCode: isChecked })}
                        />
                        {enableTrackingCode && (
                            <>
                                <SelectControl
                                    label={__('Card Type', ' dr-tracking-codes')}
                                    value={trackingCardType}
                                    options={[
                                        { label: __('Classic', ' dr-tracking-codes'), value: 'classic' },
                                        { label: __('Premier', ' dr-tracking-codes'), value: 'premier' },
                                        { label: __('Inspire', ' dr-tracking-codes'), value: 'inspire' },
                                    ]}
                                    onChange={(value) => {
                                        // Update card type; final URL preview will recompute from type + query
                                        setAttributes({ trackingCardType: value });
                                    }}
                                />
                                <TextControl
                                    label={__('Query Parameters', ' dr-tracking-codes')}
                                    help={__('Optional query string to append to the base Apply URL (e.g. pub=foo&sell=bar). You may include or omit the leading "?".', ' dr-tracking-codes')}
                                    value={trackingQuery}
                                    onChange={(value) => setAttributes({ trackingQuery: value })}
                                    placeholder={__('pub=foo&sell=bar', ' dr-tracking-codes')}
                                />
                                <div style={{ marginTop: '8px' }}>
                                    <strong>{__('Final Apply URL', ' dr-tracking-codes')}</strong>
                                    <div style={{ wordBreak: 'break-all', marginTop: '4px' }}>
                                        <code>{trackingFinalUrl || computedForSync}</code>
                                    </div>
                                </div>
                            </>
                        )}
                    </PanelBody>
                </InspectorControls>
            </>
        );
    };
}, 'withButtonControls');

addFilter(
    'editor.BlockEdit',
    'apply-now-button/with-button-controls',
    withButtonControls
);
